import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { WasteClassification } from "generated-graphql/graphql";
import { ReactNode } from "react";

import { formatWasteStreamProfileNumber } from "../Facilities/Facility/formatWasteStreamProfileNumber";
import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";

/**
 * The subset of waste-stream grid fields shown as an at-a-glance summary in
 * the dialog's current-stream box, above the tabs. Every field is
 * optional/nullable so both grids' generated row types satisfy the shape.
 */
export interface WasteStreamSummaryFieldsData {
  eManifestProfileNumber?: string | null;
  vendorProfileNumber?: string | null;
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  formCode?: string | null;
  sourceCode?: string | null;
}

/**
 * A line of fields. Both lines share one column template — at most four equal
 * columns, each at least 140px — so fields are the same width and line up
 * across lines, dropping to fewer columns as the dialog narrows.
 */
function FieldRow({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(max(140px, calc((100% - ${theme.spacing(6)}) / 4)), 1fr))`,
        columnGap: 2,
        rowGap: 1.5,
      })}
    >
      {children}
    </Box>
  );
}

function Field({
  label,
  value,
  loading = false,
}: {
  label: string;
  value: string;
  /** Still fetching: a spinner, since "—" would claim there is no value. */
  loading?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {loading ? <CircularProgress size={14} aria-label={`Loading ${label}`} /> : value || "—"}
      </Typography>
    </Box>
  );
}

export function WasteStreamSummaryFields({
  data,
  dotDescription,
  dotDescriptionLoading = false,
}: {
  data: WasteStreamSummaryFieldsData;
  /** Resolved as the stream drawer does (see `formatWasteStreamDotDescription`). */
  dotDescription: string;
  dotDescriptionLoading?: boolean;
}) {
  return (
    <Stack spacing={1.5}>
      <FieldRow>
        <Field label="Profile Number" value={formatWasteStreamProfileNumber(data)} />
        <Field label="DOT Description" value={dotDescription} loading={dotDescriptionLoading} />
        <Field label="Classification" value={prettyPrintWasteClassification(data.classification)} />
      </FieldRow>
      <FieldRow>
        <Field label="Federal Waste Code" value={(data.federalWasteCodes ?? []).join(", ")} />
        <Field label="State Waste Code" value={(data.stateWasteCodes ?? []).join(", ")} />
        <Field label="Form Code" value={data.formCode ?? ""} />
        <Field label="Source Code" value={data.sourceCode ?? ""} />
      </FieldRow>
    </Stack>
  );
}
